import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import {
  Building2, Users, Phone, Mail, MapPin, Globe, FileText, Euro,
  Plus, Search, TrendingUp, BarChart3, Calendar, Clock, AlertCircle,
  ArrowRight, Briefcase, Sparkles, Bot, ChevronRight, ExternalLink,
  Receipt, FileSpreadsheet, Package, Truck, History, Star, Target,
  Loader2, CheckCircle2, XCircle, Edit, Trash2, RefreshCw, Send,
  PhoneCall, MessageSquare, CalendarPlus, Link, User, Hash, Building,
  FilePlus, PenLine, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs statuts
const STATUT_COLORS = {
  prospect: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  client: 'bg-green-500/20 text-green-400 border-green-500/30',
  ancien_client: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  partenaire: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  fournisseur: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
};

const POTENTIEL_COLORS = {
  faible: 'bg-gray-500',
  moyen: 'bg-yellow-500',
  fort: 'bg-orange-500',
  strategique: 'bg-red-500',
};

// Sous-composant : liste des interventions GeoFolia pour un client ETA
function InterventionsClientList({ exploitationNom, apiUrl, headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!exploitationNom) { setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/api/geofolia/interventions?limit=500`, { headers });
        if (r.ok) {
          const d = await r.json();
          const all = d.interventions || [];
          const filtered = all.filter(i => 
            (i.exploitation_nom || '').toLowerCase().trim() === exploitationNom.toLowerCase().trim()
          );
          // Trier par date desc
          filtered.sort((a, b) => (b.date_debut || '').localeCompare(a.date_debut || ''));
          setItems(filtered);
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [exploitationNom, apiUrl, headers]);
  
  if (loading) return <div className="text-slate-400 text-sm py-2">Chargement...</div>;
  if (items.length === 0) return <div className="text-slate-500 text-sm py-2">Aucune intervention trouvée pour ce client.</div>;
  
  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      <div className="text-xs text-slate-400 mb-2">{items.length} interventions au total · affichage des 10 plus récentes</div>
      {items.slice(0, 10).map((i, idx) => (
        <div key={i.id || idx} className="bg-slate-900/50 rounded p-3 border border-slate-700/50" data-testid={`intervention-${idx}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">
                {i.date_debut || '?'} — {i.operation || 'Opération'}
              </p>
              <p className="text-xs text-slate-400 truncate">
                Parcelle : {i.parcelle_nom || '—'}
                {i.surface_travaillee ? ` · ${i.surface_travaillee} ha` : ''}
                {i.duree_heures ? ` · ${i.duree_heures}h` : ''}
              </p>
            </div>
            <Badge variant="outline" className="text-xs shrink-0">{i.statut || 'OK'}</Badge>
          </div>
          
          {/* Matériels */}
          {(i.materiels && i.materiels.length > 0) && (
            <div className="mt-2 text-xs">
              <span className="text-cyan-400">🚜 Matériel :</span>{' '}
              <span className="text-slate-300">{i.materiels.slice(0, 4).join(', ')}</span>
            </div>
          )}
          
          {/* Main d'œuvre */}
          {(i.main_oeuvre && i.main_oeuvre.length > 0) && (
            <div className="mt-1 text-xs">
              <span className="text-amber-400">👷 Main d'œuvre :</span>{' '}
              <span className="text-slate-300">{i.main_oeuvre.slice(0, 4).join(', ')}</span>
            </div>
          )}
          
          {/* Produits (si traitement phyto) */}
          {(i.facteurs_production && i.facteurs_production.some(f => f.type === 'produit' || f.type === 'intrant')) && (
            <div className="mt-1 text-xs">
              <span className="text-green-400">🧪 Produits :</span>{' '}
              <span className="text-slate-300">
                {i.facteurs_production
                  .filter(f => f.type === 'produit' || f.type === 'intrant')
                  .slice(0, 3)
                  .map(f => `${f.nom}${f.quantite ? ` (${f.quantite} ${f.unite || ''})` : ''}`)
                  .join(', ')}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}



// Composant Fiche Entreprise Complète
export function FicheEntreprise({ entreprise, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('infos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchingSiret, setSearchingSiret] = useState(false);
  
  // Données enrichies
  const [contacts, setContacts] = useState([]);
  const [opportunites, setOpportunites] = useState([]);
  const [factures, setFactures] = useState([]);
  const [devis, setDevis] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [contrats, setContrats] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    ca_total: 0,
    nb_factures: 0,
    nb_devis: 0,
    nb_interventions: 0,
    derniere_interaction: null
  });
  
  // Mode édition
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ ...entreprise });
  
  // Nouvelle interaction
  const [showNewInteraction, setShowNewInteraction] = useState(false);
  const [newInteraction, setNewInteraction] = useState({
    type: 'appel',
    sujet: '',
    notes: '',
    resultat: 'positif'
  });
  
  const token = localStorage.getItem('token');
  const societeId = localStorage.getItem('activeSocieteId');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Societe-Id': societeId || ''
  };
  
  // Charger toutes les données
  const fetchAllData = useCallback(async () => {
    if (!entreprise?.id) return;
    
    setLoading(true);
    try {
      // Charger en parallèle
      const [contactsRes, oppsRes, interactionsRes] = await Promise.all([
        fetch(`${API_URL}/api/crm-hub/entreprises/${entreprise.id}/contacts`, { headers }).then(r => r.json()).catch(() => ({ contacts: [] })),
        fetch(`${API_URL}/api/crm-hub/entreprises/${entreprise.id}/opportunites`, { headers }).then(r => r.json()).catch(() => ({ opportunites: [] })),
        fetch(`${API_URL}/api/crm-hub/entreprises/${entreprise.id}/interactions`, { headers }).then(r => r.json()).catch(() => ({ interactions: [] })),
      ]);
      
      setContacts(contactsRes.contacts || []);
      setOpportunites(oppsRes.opportunites || []);
      setInteractions(interactionsRes.interactions || []);
      
      // Charger factures et devis
      try {
        const facturesRes = await fetch(`${API_URL}/api/facturation/entreprise/${entreprise.id}`, { headers });
        if (facturesRes.ok) {
          const data = await facturesRes.json();
          setFactures(data.factures || []);
          setDevis(data.devis || []);
        }
      } catch (e) {
        // Pas de module facturation
      }
      
      // Charger interventions
      try {
        const intRes = await fetch(`${API_URL}/api/interventions?client_id=${entreprise.id}`, { headers });
        if (intRes.ok) {
          const data = await intRes.json();
          setInterventions(data.interventions || []);
        }
      } catch (e) {
        // Pas d'interventions
      }

      // Charger les contrats ETA du client
      try {
        const contratsRes = await fetch(`${API_URL}/api/contrats-eta/v2/client/${entreprise.id}`, { headers });
        if (contratsRes.ok) {
          const data = await contratsRes.json();
          setContrats(data.contrats || []);
        }
      } catch (e) {
        // Pas de contrats
      }
      
      // Calculer les stats
      const ca = factures.reduce((sum, f) => sum + (f.montant_ttc || 0), 0);
      setStats({
        ca_total: ca,
        nb_factures: factures.length,
        nb_devis: devis.length,
        nb_interventions: interventions.length,
        derniere_interaction: interactions[0]?.date || null
      });
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  }, [entreprise?.id]);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  // Recherche SIRET via API
  const rechercherSiret = async () => {
    if (!editData.siret || editData.siret.length < 9) {
      toast.error('SIRET invalide (14 chiffres requis)');
      return;
    }
    
    setSearchingSiret(true);
    try {
      const res = await fetch(`${API_URL}/api/sirene/search?siret=${editData.siret}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.etablissement) {
          const etab = data.etablissement;
          setEditData(prev => ({
            ...prev,
            nom: etab.nom || prev.nom,
            siren: etab.siren || prev.siren,
            forme_juridique: etab.forme_juridique || prev.forme_juridique,
            adresse: etab.adresse || prev.adresse,
            code_postal: etab.code_postal || prev.code_postal,
            ville: etab.ville || prev.ville,
            effectif: etab.effectif || prev.effectif,
            secteur_activite: etab.activite_principale || prev.secteur_activite,
          }));
          toast.success('Informations SIRET récupérées');
        }
      } else {
        toast.error('SIRET non trouvé');
      }
    } catch (error) {
      toast.error('Erreur recherche SIRET');
    } finally {
      setSearchingSiret(false);
    }
  };
  
  // Sauvegarder les modifications
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/entreprises/${entreprise.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editData)
      });
      
      if (res.ok) {
        toast.success('Entreprise mise à jour');
        setEditMode(false);
        onUpdate?.();
      } else {
        toast.error('Erreur mise à jour');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };
  
  // Ajouter une interaction
  const handleAddInteraction = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/interactions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...newInteraction,
          entreprise_id: entreprise.id,
          entreprise_nom: entreprise.nom,
          date: new Date().toISOString()
        })
      });
      
      if (res.ok) {
        toast.success('Interaction enregistrée');
        setShowNewInteraction(false);
        setNewInteraction({ type: 'appel', sujet: '', notes: '', resultat: 'positif' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Erreur enregistrement');
    }
  };
  
  // Analyse IA de l'entreprise
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  const analyserEntreprise = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/entreprises/${entreprise.id}/analyse-ia`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiAnalysis(data.analyse);
        toast.success('Analyse IA terminée');
      }
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setAnalyzing(false);
    }
  };
  
  if (!entreprise) return null;
  
  const displayData = editMode ? editData : entreprise;
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-900 text-white border-slate-700">
        <DialogHeader className="border-b border-slate-700 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Building2 className="w-8 h-8" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold">{displayData.nom}</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={cn("border", STATUT_COLORS[displayData.type_client] || STATUT_COLORS.prospect)}>
                    {displayData.type_client || 'Prospect'}
                  </Badge>
                  {displayData.potentiel && (
                    <Badge className={cn("text-white", POTENTIEL_COLORS[displayData.potentiel])}>
                      {displayData.potentiel}
                    </Badge>
                  )}
                  {displayData.siret && (
                    <span className="text-xs text-slate-400 font-mono">SIRET: {displayData.siret}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={analyserEntreprise} disabled={analyzing}>
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span className="ml-2">Analyse IA</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* KPIs */}
          <div className="grid grid-cols-5 gap-3 mt-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-3 text-center">
                <Euro className="w-5 h-5 mx-auto text-green-400 mb-1" />
                <p className="text-lg font-bold text-green-400">{stats.ca_total.toLocaleString('fr-FR')}€</p>
                <p className="text-xs text-slate-400">CA Total</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-3 text-center">
                <Receipt className="w-5 h-5 mx-auto text-blue-400 mb-1" />
                <p className="text-lg font-bold">{factures.length}</p>
                <p className="text-xs text-slate-400">Factures</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-3 text-center">
                <FileSpreadsheet className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
                <p className="text-lg font-bold">{devis.length}</p>
                <p className="text-xs text-slate-400">Devis</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-3 text-center">
                <Users className="w-5 h-5 mx-auto text-purple-400 mb-1" />
                <p className="text-lg font-bold">{contacts.length}</p>
                <p className="text-xs text-slate-400">Contacts</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-3 text-center">
                <Target className="w-5 h-5 mx-auto text-orange-400 mb-1" />
                <p className="text-lg font-bold">{opportunites.length}</p>
                <p className="text-xs text-slate-400">Opportunités</p>
              </CardContent>
            </Card>
          </div>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="infos">Informations</TabsTrigger>
            <TabsTrigger value="eta" data-testid="tab-eta-activite">Activité ETA</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="historique">Historique</TabsTrigger>
            <TabsTrigger value="affaires">Affaires ({opportunites.length})</TabsTrigger>
            <TabsTrigger value="contrats" data-testid="tab-contrats-client">Contrats ({contrats.length})</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="ia">Assistant IA</TabsTrigger>
          </TabsList>
          
          <ScrollArea className="flex-1 mt-4 pr-4">
            {/* INFORMATIONS */}
            <TabsContent value="infos" className="m-0 space-y-4">
              <div className="grid grid-cols-2 gap-6">
                {/* Colonne gauche - Infos générales */}
                <div className="space-y-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Informations légales
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {editMode ? (
                        <>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-400">SIRET</Label>
                              <div className="flex gap-2">
                                <Input 
                                  value={editData.siret || ''} 
                                  onChange={(e) => setEditData({...editData, siret: e.target.value})}
                                  className="bg-slate-700 border-slate-600"
                                  placeholder="14 chiffres"
                                />
                                <Button size="sm" onClick={rechercherSiret} disabled={searchingSiret}>
                                  {searchingSiret ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Nom</Label>
                            <Input 
                              value={editData.nom || ''} 
                              onChange={(e) => setEditData({...editData, nom: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-400">Forme juridique</Label>
                              <Input 
                                value={editData.forme_juridique || ''} 
                                onChange={(e) => setEditData({...editData, forme_juridique: e.target.value})}
                                className="bg-slate-700 border-slate-600"
                                placeholder="SARL, SAS..."
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Effectif</Label>
                              <Input 
                                type="number"
                                value={editData.effectif || ''} 
                                onChange={(e) => setEditData({...editData, effectif: parseInt(e.target.value) || null})}
                                className="bg-slate-700 border-slate-600"
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <InfoRow label="SIRET" value={displayData.siret} icon={<Hash className="w-4 h-4" />} />
                          <InfoRow label="SIREN" value={displayData.siren} icon={<Hash className="w-4 h-4" />} />
                          <InfoRow label="Forme juridique" value={displayData.forme_juridique} icon={<Building className="w-4 h-4" />} />
                          <InfoRow label="Effectif" value={displayData.effectif ? `${displayData.effectif} salariés` : null} icon={<Users className="w-4 h-4" />} />
                          <InfoRow label="Secteur" value={displayData.secteur_activite} icon={<Briefcase className="w-4 h-4" />} />
                        </>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Coordonnées
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {editMode ? (
                        <>
                          <div>
                            <Label className="text-xs text-slate-400">Adresse</Label>
                            <Input 
                              value={editData.adresse || ''} 
                              onChange={(e) => setEditData({...editData, adresse: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-400">Code postal</Label>
                              <Input 
                                value={editData.code_postal || ''} 
                                onChange={(e) => setEditData({...editData, code_postal: e.target.value})}
                                className="bg-slate-700 border-slate-600"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Ville</Label>
                              <Input 
                                value={editData.ville || ''} 
                                onChange={(e) => setEditData({...editData, ville: e.target.value})}
                                className="bg-slate-700 border-slate-600"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Téléphone</Label>
                            <Input 
                              value={editData.telephone || ''} 
                              onChange={(e) => setEditData({...editData, telephone: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Email</Label>
                            <Input 
                              value={editData.email || ''} 
                              onChange={(e) => setEditData({...editData, email: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Site web</Label>
                            <Input 
                              value={editData.site_web || ''} 
                              onChange={(e) => setEditData({...editData, site_web: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <InfoRow label="Adresse" value={displayData.adresse} icon={<MapPin className="w-4 h-4" />} />
                          <InfoRow label="Ville" value={displayData.ville && displayData.code_postal ? `${displayData.code_postal} ${displayData.ville}` : displayData.ville} />
                          <InfoRow label="Téléphone" value={displayData.telephone} icon={<Phone className="w-4 h-4" />} copyable clickable />
                          <InfoRow label="Email" value={displayData.email} icon={<Mail className="w-4 h-4" />} copyable clickable />
                          <InfoRow label="Site web" value={displayData.site_web} icon={<Globe className="w-4 h-4" />} link />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Colonne droite - Infos commerciales */}
                <div className="space-y-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Informations commerciales
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {editMode ? (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-400">Type client</Label>
                              <Select value={editData.type_client || 'prospect'} onValueChange={(v) => setEditData({...editData, type_client: v})}>
                                <SelectTrigger className="bg-slate-700 border-slate-600">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="prospect">Prospect</SelectItem>
                                  <SelectItem value="client">Client</SelectItem>
                                  <SelectItem value="ancien_client">Ancien client</SelectItem>
                                  <SelectItem value="partenaire">Partenaire</SelectItem>
                                  <SelectItem value="fournisseur">Fournisseur</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-400">Potentiel</Label>
                              <Select value={editData.potentiel || 'moyen'} onValueChange={(v) => setEditData({...editData, potentiel: v})}>
                                <SelectTrigger className="bg-slate-700 border-slate-600">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="faible">Faible</SelectItem>
                                  <SelectItem value="moyen">Moyen</SelectItem>
                                  <SelectItem value="fort">Fort</SelectItem>
                                  <SelectItem value="strategique">Stratégique</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">CA estimé</Label>
                            <Input 
                              type="number"
                              value={editData.ca_estime || ''} 
                              onChange={(e) => setEditData({...editData, ca_estime: parseFloat(e.target.value) || 0})}
                              className="bg-slate-700 border-slate-600"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400">Notes</Label>
                            <Textarea 
                              value={editData.notes || ''} 
                              onChange={(e) => setEditData({...editData, notes: e.target.value})}
                              className="bg-slate-700 border-slate-600"
                              rows={3}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <InfoRow label="CA estimé" value={displayData.ca_estime ? `${displayData.ca_estime.toLocaleString('fr-FR')}€` : null} icon={<Euro className="w-4 h-4" />} />
                          <InfoRow label="Segment" value={displayData.segment} icon={<Target className="w-4 h-4" />} />
                          <InfoRow label="Source" value={displayData.source} icon={<Link className="w-4 h-4" />} />
                          {displayData.notes && (
                            <div className="mt-2 p-2 bg-slate-700/50 rounded text-sm text-slate-300">
                              {displayData.notes}
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Analyse IA */}
                  {aiAnalysis && (
                    <Card className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 border-purple-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-400" />
                          Analyse IA
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-slate-300 whitespace-pre-wrap">
                          {aiAnalysis}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Actions rapides */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Actions rapides</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-2">
                      <Button variant="outline" size="sm" className="justify-start" onClick={() => setShowNewInteraction(true)}>
                        <PhoneCall className="w-4 h-4 mr-2" />
                        Appeler
                      </Button>
                      <Button variant="outline" size="sm" className="justify-start">
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                      <Button variant="outline" size="sm" className="justify-start">
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        RDV
                      </Button>
                      <Button variant="outline" size="sm" className="justify-start">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Devis
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {editMode && (
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditData({...entreprise}); }}>
                    Annuler
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Enregistrer
                  </Button>
                </div>
              )}
            </TabsContent>
            
            {/* ACTIVITÉ ETA (facturation Drive + parcellaire GeoFolia + interventions) */}
            <TabsContent value="eta" className="m-0 space-y-4">
              {/* BLOC FACTURATION 2026 */}
              {entreprise?.facturation_drive ? (
                <Card className="bg-gradient-to-br from-emerald-900/30 to-slate-800 border-emerald-700/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Euro className="w-4 h-4 text-emerald-400" />
                        Facturation 2026 (Comptabilité Drive)
                      </h3>
                      <Badge className="bg-emerald-500/20 text-emerald-300">
                        {entreprise.facturation_drive.nb_factures} factures
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <p className="text-xs text-slate-400">CA TTC</p>
                        <p className="text-lg font-bold text-white">
                          {(entreprise.facturation_drive.ca_ttc_total || 0).toLocaleString('fr-FR')}€
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">✅ Payé</p>
                        <p className="text-lg font-bold text-emerald-400">
                          {(entreprise.facturation_drive.paye_ttc || 0).toLocaleString('fr-FR')}€
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">⏳ En attente</p>
                        <p className="text-lg font-bold text-amber-400">
                          {(entreprise.facturation_drive.en_attente_ttc || 0).toLocaleString('fr-FR')}€
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">💸 Avoirs</p>
                        <p className="text-lg font-bold text-slate-400">
                          {(entreprise.facturation_drive.avoir_ttc || 0).toLocaleString('fr-FR')}€
                        </p>
                      </div>
                    </div>
                    {entreprise.facturation_drive.derniere_facture_date && (
                      <p className="text-xs text-slate-400 mt-3">
                        Dernière facture : {entreprise.facturation_drive.derniere_facture_date}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-4 text-center text-slate-500 text-sm">
                    Aucune facture 2026 dans le Drive comptabilité pour ce client.
                  </CardContent>
                </Card>
              )}

              {/* BLOC PARCELLAIRE GeoFolia */}
              {entreprise?.geofolia_match ? (
                <Card className="bg-gradient-to-br from-indigo-900/30 to-slate-800 border-indigo-700/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Package className="w-4 h-4 text-indigo-400" />
                        Parcellaire & interventions (GeoFolia)
                      </h3>
                      <Badge className="bg-indigo-500/20 text-indigo-300">
                        {entreprise.geofolia_match.nb_interventions} interventions
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-slate-400">Surface</p>
                        <p className="text-lg font-bold text-white">{entreprise.geofolia_match.surface_totale_ha} ha</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Parcelles</p>
                        <p className="text-lg font-bold text-white">{entreprise.geofolia_match.nb_parcelles}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Cultures</p>
                        <p className="text-lg font-bold text-white">{(entreprise.geofolia_match.cultures || []).length}</p>
                      </div>
                    </div>
                    {entreprise.geofolia_match.cultures?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-slate-400 mb-1">Cultures principales :</p>
                        <div className="flex flex-wrap gap-1">
                          {entreprise.geofolia_match.cultures.slice(0, 10).map((c, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-indigo-700/50">
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-4 text-center text-slate-500 text-sm">
                    Pas de parcellaire GeoFolia intégré pour ce client (c'est un prospect ou pas encore synchronisé).
                  </CardContent>
                </Card>
              )}

              {/* LISTE DES INTERVENTIONS (derniers 10) */}
              {entreprise?.geofolia_match?.exploitation_nom && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4 text-cyan-400" />
                      Dernières interventions (matériel + main d'œuvre)
                    </h3>
                    <InterventionsClientList 
                      exploitationNom={entreprise.geofolia_match.exploitation_nom}
                      apiUrl={API_URL}
                      headers={headers}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="contacts" className="m-0 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Contacts de l'entreprise</h3>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter contact
                </Button>
              </div>
              
              {contacts.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-8 text-center text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun contact associé</p>
                    <Button variant="link" className="text-blue-400 mt-2">
                      + Ajouter un contact
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {contacts.map((contact) => (
                    <Card key={contact.id} className="bg-slate-800 border-slate-700 hover:border-blue-500/50 cursor-pointer">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium">{contact.prenom} {contact.nom}</p>
                            <p className="text-sm text-slate-400">{contact.poste || 'Non renseigné'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {contact.telephone && (
                            <Button variant="ghost" size="sm">
                              <Phone className="w-4 h-4" />
                            </Button>
                          )}
                          {contact.email && (
                            <Button variant="ghost" size="sm">
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                          {contact.est_decideur && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                              <Star className="w-3 h-3 mr-1" />
                              Décideur
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* HISTORIQUE */}
            <TabsContent value="historique" className="m-0 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Historique des interactions</h3>
                <Button size="sm" onClick={() => setShowNewInteraction(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle interaction
                </Button>
              </div>
              
              <div className="space-y-3">
                {interactions.length === 0 ? (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-8 text-center text-slate-400">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune interaction enregistrée</p>
                    </CardContent>
                  </Card>
                ) : (
                  interactions.map((interaction) => (
                    <Card key={interaction.id} className="bg-slate-800 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center",
                            interaction.type === 'appel' && "bg-green-500/20",
                            interaction.type === 'email' && "bg-blue-500/20",
                            interaction.type === 'rdv' && "bg-purple-500/20",
                            interaction.type === 'note' && "bg-yellow-500/20"
                          )}>
                            {interaction.type === 'appel' && <PhoneCall className="w-4 h-4 text-green-400" />}
                            {interaction.type === 'email' && <Mail className="w-4 h-4 text-blue-400" />}
                            {interaction.type === 'rdv' && <Calendar className="w-4 h-4 text-purple-400" />}
                            {interaction.type === 'note' && <FileText className="w-4 h-4 text-yellow-400" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium">{interaction.sujet}</p>
                              <span className="text-xs text-slate-400">
                                {new Date(interaction.date).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            {interaction.notes && (
                              <p className="text-sm text-slate-400 mt-1">{interaction.notes}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
            
            {/* AFFAIRES */}
            <TabsContent value="affaires" className="m-0 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Opportunités en cours</h3>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle opportunité
                </Button>
              </div>
              
              {opportunites.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-8 text-center text-slate-400">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune opportunité</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {opportunites.map((opp) => (
                    <Card key={opp.id} className="bg-slate-800 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{opp.nom}</p>
                            <p className="text-sm text-slate-400">Étape: {opp.etape}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-400">{opp.montant_estime?.toLocaleString('fr-FR')}€</p>
                            <p className="text-xs text-slate-400">{opp.probabilite}% de chance</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* CONTRATS ETA */}
            <TabsContent value="contrats" className="m-0 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  Contrats de prestations
                </h3>
                <Button
                  size="sm"
                  data-testid="btn-nouveau-contrat-eta"
                  onClick={() => {
                    if (entreprise?.id) {
                      window.location.href = `/contrats-eta-simple?client_id=${entreprise.id}&client_nom=${encodeURIComponent(entreprise.nom || '')}`;
                    }
                  }}
                >
                  <FilePlus className="w-4 h-4 mr-2" />
                  Nouveau contrat
                </Button>
              </div>
              
              {contrats.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-8 text-center text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun contrat pour ce client</p>
                    <p className="text-xs mt-2">Créez un contrat de prestations ETA (viticulture, communal, conseil, suivi, audit).</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {contrats.map((c) => {
                    const statutColor = {
                      brouillon: 'bg-gray-500/20 text-gray-300',
                      envoye: 'bg-blue-500/20 text-blue-400',
                      signe: 'bg-green-500/20 text-green-400',
                      refuse: 'bg-red-500/20 text-red-400',
                      annule: 'bg-orange-500/20 text-orange-400',
                    }[c.statut] || 'bg-slate-500/20 text-slate-300';
                    return (
                      <Card key={c.id} className="bg-slate-800 border-slate-700" data-testid={`contrat-row-${c.id}`}>
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-400" />
                            <div>
                              <p className="font-medium">{c.numero || c.titre}</p>
                              <p className="text-xs text-slate-400">
                                {c.created_at && new Date(c.created_at).toLocaleDateString('fr-FR')}
                                {c.titre && c.numero && ` · ${c.titre}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-bold">{(c.totaux?.total_ttc ?? c.total_estime_ttc ?? 0).toLocaleString('fr-FR')}€ TTC</p>
                              <Badge className={statutColor}>
                                {c.statut === 'signe' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                {c.statut === 'envoye' && <Send className="w-3 h-3 mr-1" />}
                                {c.statut === 'brouillon' && <PenLine className="w-3 h-3 mr-1" />}
                                {c.statut || 'brouillon'}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`btn-pdf-contrat-${c.id}`}
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/contrats-eta/v2/${c.id}/pdf`, { headers });
                                  if (res.ok) {
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                  } else {
                                    toast.error('PDF indisponible');
                                  }
                                } catch (e) {
                                  toast.error('Erreur ouverture PDF');
                                }
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            
            {/* DOCUMENTS */}
            <TabsContent value="documents" className="m-0 space-y-4">
              <Tabs defaultValue="factures" className="w-full">
                <TabsList className="bg-slate-800">
                  <TabsTrigger value="factures">Factures ({factures.length})</TabsTrigger>
                  <TabsTrigger value="devis">Devis ({devis.length})</TabsTrigger>
                  <TabsTrigger value="interventions">Interventions ({interventions.length})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="factures" className="mt-4">
                  {factures.length === 0 ? (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardContent className="py-8 text-center text-slate-400">
                        <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune facture</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {factures.map((facture) => (
                        <Card key={facture.id} className="bg-slate-800 border-slate-700">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Receipt className="w-5 h-5 text-blue-400" />
                              <div>
                                <p className="font-medium">{facture.numero || `FAC-${facture.id?.slice(-6)}`}</p>
                                <p className="text-xs text-slate-400">
                                  {facture.date_emission && new Date(facture.date_emission).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{facture.montant_ttc?.toLocaleString('fr-FR')}€</p>
                              <Badge className={facture.statut === 'payee' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                {facture.statut || 'En attente'}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="devis" className="mt-4">
                  {devis.length === 0 ? (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardContent className="py-8 text-center text-slate-400">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucun devis</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {devis.map((d) => (
                        <Card key={d.id} className="bg-slate-800 border-slate-700">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileSpreadsheet className="w-5 h-5 text-yellow-400" />
                              <div>
                                <p className="font-medium">{d.numero || `DEV-${d.id?.slice(-6)}`}</p>
                                <p className="text-xs text-slate-400">
                                  {d.date_emission && new Date(d.date_emission).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{d.montant_ttc?.toLocaleString('fr-FR')}€</p>
                              <Badge className={
                                d.statut === 'accepte' ? 'bg-green-500/20 text-green-400' : 
                                d.statut === 'refuse' ? 'bg-red-500/20 text-red-400' : 
                                'bg-blue-500/20 text-blue-400'
                              }>
                                {d.statut || 'En attente'}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="interventions" className="mt-4">
                  {interventions.length === 0 ? (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardContent className="py-8 text-center text-slate-400">
                        <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune intervention</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {interventions.map((int) => (
                        <Card key={int.id} className="bg-slate-800 border-slate-700">
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Truck className="w-5 h-5 text-orange-400" />
                              <div>
                                <p className="font-medium">{int.operation || int.type_intervention}</p>
                                <p className="text-xs text-slate-400">
                                  {int.date && new Date(int.date).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              {int.heures_travaillees && (
                                <p className="font-bold">{int.heures_travaillees}h</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>
            
            {/* ASSISTANT IA */}
            <TabsContent value="ia" className="m-0 space-y-4">
              <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="w-5 h-5 text-purple-400" />
                    Agent Commercial IA
                  </CardTitle>
                  <CardDescription>
                    Votre assistant intelligent pour cette entreprise
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" className="justify-start h-auto py-3" onClick={analyserEntreprise} disabled={analyzing}>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-purple-400" />}
                          <span className="font-medium">Analyser l'entreprise</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Profil, potentiel, recommandations</p>
                      </div>
                    </Button>
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <Send className="w-4 h-4 text-blue-400" />
                          <span className="font-medium">Générer un email</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Email personnalisé avec contexte</p>
                      </div>
                    </Button>
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-yellow-400" />
                          <span className="font-medium">Préparer un devis</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Devis automatique selon historique</p>
                      </div>
                    </Button>
                    <Button variant="outline" className="justify-start h-auto py-3">
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-orange-400" />
                          <span className="font-medium">Stratégie de vente</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Plan d'action personnalisé</p>
                      </div>
                    </Button>
                  </div>
                  
                  {aiAnalysis && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Dernière analyse</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">{aiAnalysis}</p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>
        
        {/* Dialog nouvelle interaction */}
        <Dialog open={showNewInteraction} onOpenChange={setShowNewInteraction}>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle>Nouvelle interaction</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={newInteraction.type} onValueChange={(v) => setNewInteraction({...newInteraction, type: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appel">Appel</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="rdv">RDV</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sujet</Label>
                <Input 
                  value={newInteraction.sujet}
                  onChange={(e) => setNewInteraction({...newInteraction, sujet: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction({...newInteraction, notes: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  rows={3}
                />
              </div>
              <div>
                <Label>Résultat</Label>
                <Select value={newInteraction.resultat} onValueChange={(v) => setNewInteraction({...newInteraction, resultat: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positif">Positif</SelectItem>
                    <SelectItem value="neutre">Neutre</SelectItem>
                    <SelectItem value="negatif">Négatif</SelectItem>
                    <SelectItem value="rappeler">À rappeler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewInteraction(false)}>Annuler</Button>
              <Button onClick={handleAddInteraction}>Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// Composant InfoRow
function InfoRow({ label, value, icon, copyable, clickable, link }) {
  if (!value) return null;
  
  const handleClick = () => {
    if (copyable) {
      navigator.clipboard.writeText(value);
      toast.success('Copié !');
    } else if (link) {
      window.open(value.startsWith('http') ? value : `https://${value}`, '_blank');
    } else if (clickable) {
      if (value.includes('@')) {
        window.location.href = `mailto:${value}`;
      } else {
        window.location.href = `tel:${value}`;
      }
    }
  };
  
  return (
    <div className={cn(
      "flex items-center justify-between py-1",
      (copyable || clickable || link) && "cursor-pointer hover:bg-slate-700/30 rounded px-2 -mx-2"
    )} onClick={handleClick}>
      <div className="flex items-center gap-2 text-slate-400">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

export default FicheEntreprise;
